// =============================================================================
// elementStepProgress.ts — Supabase persistence for map WBS step state.
// Strategy: Supabase = source of truth, localStorage = offline cache.
// =============================================================================

import { supabase, isDemoMode } from './supabase'

const LS_KEY_GENERIC = 'nv_generic_step_overrides'

function loadLocalOverrides(): Record<string, Record<string, string>> {
  try {
    const raw = localStorage.getItem(LS_KEY_GENERIC)
    return raw ? JSON.parse(raw) : {}
  } catch { return {} }
}

function saveLocalOverrides(data: Record<string, Record<string, string>>): void {
  try { localStorage.setItem(LS_KEY_GENERIC, JSON.stringify(data)) } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// syncFromSupabase — call once at map-ready to pull latest state from server.
// Merges remote records into localStorage (remote wins on conflict).
// ---------------------------------------------------------------------------

export async function syncFromSupabase(): Promise<void> {
  if (isDemoMode) return
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from('element_step_progress')
      .select('element_key, feature_id, step_code')
    if (error || !data) return
    const merged = loadLocalOverrides()
    for (const row of data as { element_key: string; feature_id: string; step_code: string }[]) {
      if (!merged[row.element_key]) merged[row.element_key] = {}
      merged[row.element_key][row.feature_id] = row.step_code
    }
    saveLocalOverrides(merged)
  } catch { /* offline — keep localStorage as-is */ }
}

// ---------------------------------------------------------------------------
// persistToSupabase — fire-and-forget upsert after every local save.
// ---------------------------------------------------------------------------

export function persistToSupabase(elementKey: string, featureId: string, stepCode: string): void {
  if (isDemoMode) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(supabase as any)
    .from('element_step_progress')
    .upsert(
      { element_key: elementKey, feature_id: featureId, step_code: stepCode },
      { onConflict: 'element_key,feature_id' },
    )
    .then(({ error }: { error: { message: string } | null }) => {
      if (error) console.warn('[elementStepProgress] upsert failed:', error.message)
    })
}
